import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
// import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition"; // Valtech Custom Code
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
// import Button, { ButtonPresentationProps } from "@insite/mobius/Button"; // Valtech Custom Code
import { LinkPresentationProps } from "@insite/mobius/Link";
// import Typography from "@insite/mobius/Typography"; // Valtech Custom Code
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

/* Valtech Custom Code */
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, resolveColor } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import ResponsiveButton from "@store/Components/Common/ResponsiveButton";
import { COLORS, TYPOGRAPHY } from "@store/theme/design";
import { callToActionSpacing, contentSpacingStyles, siteWidthStyles } from "@store/theme/mixins";
import { bannerSpacingStyles } from "@store/theme/mixins/banner";
import { polyfillCss } from "@store/theme/polyfills";
import { ButtonColorPreset, buttonGetColorPreset } from "@store/theme/props/button";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import Color from "color";

const defaultOverlayColor = Color(COLORS.black).alpha(0.6).toString();
const defaultBackgroundColor = Color(COLORS.secondary.base).alpha(1).toString();

const imageSize = {
    mobile: {
        width: 768,
        height: 550,
    },
    desktop: {
        width: 1920,
        height: 700,
    },
};
/* Valtech Custom Code */

/*
    Valtech Removed Fields
    ----------------------------------
    background
    bannerWidth
    partialOverlay
    partialOverlayPositioning
    focalPoint
    minimumHeight
    disableButton
    responsiveFontSizes
    contentPadding
    centerTextVertically
    customFontSizes
    normalFontSize
    h1FontSize
    h2FontSize
    h3FontSize
    h4FontSize
    h5FontSize
    h6FontSize
    customCSS,
*/

const enum fields {
    image = "image",
    backgroundColor = "backgroundColor",
    heading = "heading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    imageOverlay = "imageOverlay",
    // Valtech Custom Code
    mobileImage = "mobileImage",
    horizontalAlignment = "horizontalAlignment",
    invertColor = "invertColor",
}

/* Valtech Custom Code */
export interface BannerFields {
    [fields.image]: string;
    [fields.backgroundColor]: string;
    [fields.heading]: string;
    [fields.subheading]: string;
    [fields.buttonLabel]: string;
    [fields.buttonLink]: LinkFieldValue;
    [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
    [fields.imageOverlay]: string;
    // Valtech Custom Code
    [fields.mobileImage]: boolean;
    [fields.horizontalAlignment]: string;
    [fields.invertColor]: boolean;
}
/* Valtech Custom Code */

interface OwnProps extends WidgetProps {
    fields: BannerFields; // Valtech Custom Code
    extendedStyles?: BannerStyles;
    // Valtech Custom Code
    hideMargin?: boolean;
    tabIndex?: number;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    // Valtech Custom Code
    heading?: TypographyPresentationProps;
    subheading?: TypographyPresentationProps;
    imageWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    contentGridContainer?: GridContainerProps;
    contentGridItem?: GridItemProps;
    buttonWrapper?: InjectableCss;
}

export const bannerStyles: BannerStyles = {
    heading: {
        forwardAs: "h2",
        color: "inherit",
        weight: TYPOGRAPHY.weight.light,
        transform: "uppercase",
        css: css`
            ${contentSpacingStyles("0.5em", "0.25em")}
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: [1.15, null, 1.05, 1], properties: ["line-height"] },
                        { values: [32, 35, 42, 56, 70], properties: ["font-size"], unit: "px" },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    subheading: {
        variant: "p",
        size: TYPOGRAPHY.size.base,
        lineHeight: TYPOGRAPHY.lineHeight.small,
        css: css`
            letter-spacing: ${TYPOGRAPHY.letterSpacing.large};

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: [null, null, null, TYPOGRAPHY.size.large, TYPOGRAPHY.size.larger],
                            properties: ["font-size"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    buttonWrapper: {
        css: css`
            ${callToActionSpacing()}
        `,
    },
};

export const Banner: FC<OwnProps> = ({
    fields,
    extendedStyles,
    // Valtech Custom Code
    hideMargin,
    tabIndex,
}) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    /*
        Valtech Removed
        ------------------------
        backgroundStyles
        focalPointStyles
        minimumHeightStyles
        overlayPositioningStyles
    */

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    /* Valtech Removed - fontSizeStyles */
    /* Valtech Removed - customCssWrapper */

    /* Valtech Custom Code */
    const wrapperStyles = {
        css: css`
            ${!hideMargin ? bannerSpacingStyles("margin") : null}
            ${fields.backgroundColor ? `background-color: ${fields.backgroundColor};` : null}
            color: ${({ theme }) => resolveColor(fields.invertColor ? "text.contrast" : "text", theme)};
            position: relative;
            ${styles.wrapper?.css}
        `,
    };

    const imageWrapperStyles = {
        css: css`
            position: relative;
            background-size: cover;
            padding-top: ${(imageSize.mobile.height / imageSize.mobile.width) * 100}%;
            ${fields.mobileImage ? `background-image: url(${fields.mobileImage});` : null}

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        css`
                            padding-top: ${(imageSize.desktop.height / imageSize.desktop.width) * 100}%;
                            ${fields.image ? `background-image: url(${fields.image});` : null}
                        `,
                        polyfillCss`
                            &::before {
                                content: "";
                                position: absolute;
                                top: 0;
                                right: 0;
                                bottom: 0;
                                left: 0;
                                ${() => {
                                    if (fields.image && fields.imageOverlay) {
                                        return `
                                            background-color: ${fields.imageOverlay};
                                            background: linear-gradient(
                                                ${fields.horizontalAlignment === "right" ? "90deg" : "-90deg"},
                                                ${Color(fields.imageOverlay).alpha(0).toString()} 0%,
                                                ${fields.imageOverlay} 100%
                                            );
                                        `;
                                    }
                                }}
                                ${styles.overlayWrapper?.css}
                            }
                        `,
                    ],
                    mediaOptions.min,
                )}

            ${styles.imageWrapper?.css}
        `,
    };

    const contentWrapperStyles = {
        css: css`
            ${siteWidthStyles()}
            overflow: hidden;

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        css`
                            padding: 25px;
                        `,
                        css`
                            padding: 30px;
                        `,
                        css`
                            padding: 30px 64px;
                        `,
                        css`
                            padding: 0 64px;
                        `,
                    ],
                    mediaOptions.minMax,
                )}

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            position: absolute;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            left: 0;
                            display: flex;
                            align-items: center;
                        `,
                    ],
                    mediaOptions.min,
                )}

            ${styles.contentWrapper?.css}
        `,
    };

    const contentGridContainer = {
        ...styles.contentGridContainer,
        offsetCss: css`
            ${fields.horizontalAlignment === "right" ? "justify-content: flex-end;" : null}
            ${styles.contentGridContainer?.offsetCss}
        `,
    } as GridContainerProps;

    const subHeading = {
        ...styles.subheading,
        css: css`
            ${fields.horizontalAlignment === "right" ? "padding-left: 10%;" : "padding-right: 10%;"}
            ${styles.subheading?.css}
        `,
    } as TypographyPresentationProps;

    const contentGridItem = {
        width: [12, 12, 5, 5, 5],
        ...styles.contentGridItem,
        css: css`
            display: block;
            ${fields.horizontalAlignment === "right" ? "text-align: right;" : null}
            ${styles.contentGridItem?.css}
        `,
    } as GridItemProps;

    const buttonProps: ButtonProps = {
        ...styles.bannerButton,
        variant: fields.variant,
        onClick,
        ...(fields.invertColor ? buttonGetColorPreset(ButtonColorPreset.Invert) : null),
        tabIndex,
    };
    /* Valtech Custom Code */

    return (
        <>
            {/* Valtech Custom Code */}
            <StyledWrapper {...wrapperStyles}>
                <StyledWrapper {...imageWrapperStyles}></StyledWrapper>
                <StyledWrapper {...contentWrapperStyles}>
                    <GridContainer {...contentGridContainer}>
                        <GridItem {...contentGridItem}>
                            {fields.heading ? <Typography {...styles.heading}>{fields.heading}</Typography> : null}
                            {fields.subheading ? <Typography {...subHeading}>{fields.subheading}</Typography> : null}
                            {url ? (
                                <ResponsiveButton buttonProps={buttonProps} {...styles.buttonWrapper}>
                                    {fields.buttonLabel || title || url}
                                </ResponsiveButton>
                            ) : null}
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
            {/* Valtech Custom Code */}
        </>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

/* Valtech Removed - basicTab, settingsTab, advancedTab */

/* Valtech Custom Code */
export const BannerFieldDefinitions: Array<FieldDefinition> = [
    {
        fieldType: "General",
        name: fields.backgroundColor,
        editorTemplate: "ColorPickerField",
        displayName: "Background Color", // Valtech Custom Code
        tooltip: `Default: ${defaultBackgroundColor}, Alternative: rgb(36, 76, 90)`, // Valtech Custom Code
        defaultValue: defaultBackgroundColor, // Valtech Custom Code
        // isVisible: widget => widget.fields.background === "color", // Valtech Custom Code
        // tab: basicTab, // Valtech Custom Code
    },
    /* Valtech Custom Code */
    {
        name: fields.invertColor,
        displayName: "White Text Color",
        editorTemplate: "CheckboxField",
        fieldType: "General",
        defaultValue: true,
        // tab: basicTab, // Valtech Custom Code
    },
    {
        fieldType: "Translatable",
        name: fields.mobileImage,
        displayName: "Mobile Image",
        tooltip: `${imageSize.mobile.width} x ${imageSize.mobile.height}`,
        editorTemplate: "ImagePickerField",
        defaultValue: "",
        // tab: basicTab, // Valtech Custom Code
    },
    /* Valtech Custom Code */
    {
        fieldType: "Translatable",
        name: fields.image,
        displayName: "Desktop Image", // Valtech Custom Code
        tooltip: `${imageSize.desktop.width} x ${imageSize.desktop.height}`, // Valtech Custom Code
        editorTemplate: "ImagePickerField",
        defaultValue: "",
        // isVisible: widget => widget.fields.background === "image", // Valtech Custom Code
        // tab: basicTab, // Valtech Custom Code
    },
    {
        fieldType: "General",
        name: fields.imageOverlay,
        displayName: "Desktop Overlay Color", // Valtech Custom Code
        editorTemplate: "ColorPickerField",
        tooltip: `Default: ${defaultOverlayColor}`, // Valtech Custom Code
        defaultValue: defaultOverlayColor, // Valtech Custom Code
        isVisible: widget => widget.fields.image, // Valtech Custom Code
        // tab: basicTab, // Valtech Custom Code
    },
    /* Valtech Moved Up - backgroundColor */
    {
        fieldType: "Translatable",
        name: fields.heading,
        editorTemplate: "TextField", // Valtech Custom Code
        displayName: "Title", // Valtech Custom Code
        defaultValue: "",
        /* Abolunet Custom Code 
        extendedConfig: { height: 100 },
        expandedToolbarButtons: {
            moreMisc: {},
            code: {},
        },
        */
        // tab: basicTab, // Valtech Custom Code
    },
    {
        fieldType: "Translatable",
        name: fields.subheading,
        editorTemplate: "MultilineTextField", // Valtech Custom Code
        displayName: "Content", // Valtech Custom Code
        defaultValue: "",
        /* Abolunet Custom Code
        extendedConfig: { height: 170 },
        expandedToolbarButtons: {
            moreMisc: {},
            code: {},
        },
        */
        // tab: basicTab, // Valtech Custom Code
    },
    {
        fieldType: "General",
        name: fields.buttonLink,
        displayName: "Button Link",
        editorTemplate: "LinkField",
        defaultValue: { type: "Page", value: "" },
        // tab: basicTab, // Valtech Custom Code
    },
    {
        fieldType: "Translatable",
        name: fields.buttonLabel,
        editorTemplate: "TextField",
        displayName: "Button Label",
        defaultValue: "",
        // tab: basicTab, // Valtech Custom Code
        isVisible: widget => widget.fields.buttonLink.value, // Asolunet Custom Code
    },
    /* Valtech Moved Up - buttonLink */
    {
        name: fields.buttonVariant,
        displayName: "Button Variant",
        editorTemplate: "DropDownField",
        options: [
            { displayName: "Primary", value: "primary" },
            { displayName: "Secondary", value: "secondary" },
            { displayName: "Tertiary", value: "tertiary" },
        ],
        hideEmptyOption: true,
        defaultValue: "primary",
        fieldType: "General",
        // tab: basicTab, // Valtech Custom Code
        isVisible: widget => widget.fields.buttonLink.value, // Asolunet Custom Code
    },
    /* Valtech Custom Code */
    {
        name: fields.horizontalAlignment,
        displayName: "Alignment",
        editorTemplate: "DropDownField",
        options: [
            { displayName: "Left", value: "left" },
            { displayName: "Right", value: "right" },
        ],
        hideEmptyOption: true,
        fieldType: "General",
        defaultValue: "left",
    },
    /* Valtech Custom Code */
];
/* Valtech Custom Code */

const banner: WidgetModule = {
    component: Banner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: BannerFieldDefinitions,
    },
};

export default banner;
